import { create } from 'zustand';
import { devtools } from 'zustand/middleware';

// Types for the new research system
export interface ResearchProject {
  id: string;
  name: string;
  asin?: string;
  brandName: string;
  productUrl: string;
  targetAudience: string;
  tone: string;
  goals: string;
  createdAt: string;
  updatedAt: string;
}

export interface ResearchData {
  overview: OverviewData | null;
  demographics: DemographicsData | null;
  competitor: CompetitorData | null;
  reviews: ReviewsData | null;
  discovery: DiscoveryData | null;
  keywords: KeywordsData | null;
}

export interface OverviewData {
  top_suggestions: SuggestionItem[];
  strengths: StrengthWeaknessItem[];
  weaknesses: StrengthWeaknessItem[];
  buying_factors: BuyingFactorItem[];
  keywords: KeywordItem[];
}

export interface SuggestionItem {
  title: string;
  description: string;
  impact_percentage: number;
  impact_level: 'High' | 'Medium' | 'Low';
  tags: string[];
  reasoning: string;
  priority: number;
}

export interface StrengthWeaknessItem {
  feature: string;
  percentage: number;
  description: string;
  evidence: string;
  category: 'strength' | 'weakness';
}

export interface BuyingFactorItem {
  factor: string;
  percentage: number;
  impact: string;
  color: 'green' | 'yellow' | 'red';
}

export interface KeywordItem {
  keyword: string;
  placement: string;
  action: 'replace' | 'add';
  reasoning: string;
  search_volume?: number;
  competition?: string;
}

export interface DemographicsData {
  gender_distribution: Record<string, number>;
  age_distribution: Record<string, number>;
  geographic_distribution: Record<string, number>;
  usage_scenarios: Array<{ scenario: string; percentage: number; context: string; key_reason: string }>;
  purchase_drivers: Array<{ trigger_event: string; core_motivation: string; driver_type: string; percentage: number }>;
  concerns_objections: Array<{ concern: string; reason: string; percentage: number }>;
  core_values: Array<{ value: string; importance: string; aligned_icp: string; percentage: number }>;
  purchase_behavior: any[];
  customer_psychology: any[];
}

export interface CompetitorData {
  direct_competitors: any[];
  indirect_competitors: any[];
  market_positioning: any;
  strengths_weaknesses: Record<string, string[]>;
  pricing_strategies: any[];
}

export interface ReviewsData {
  total_reviews: number;
  average_rating: number;
  positive_reviews: any[];
  negative_reviews: any[];
  sentiment_analysis: any;
  common_themes: any[];
}

export interface DiscoveryData {
  search_patterns: any[];
  discovery_channels: any[];
  conversion_optimization: any[];
  market_trends: any[];
}

export interface KeywordsData {
  primary_keywords: KeywordItem[];
  long_tail_keywords: KeywordItem[];
  competitor_keywords: KeywordItem[];
  search_volume_data: Record<string, number>;
  ranking_opportunities: any[];
}

export interface ResearchState {
  // Current project
  currentProject: ResearchProject | null;
  
  // Research data
  researchData: ResearchData | null;
  
  // Loading states
  isLoading: boolean;
  isGenerating: boolean;
  
  // Error state
  error: string | null;
  
  // Actions
  setCurrentProject: (project: ResearchProject | null) => void;
  setResearchData: (data: ResearchData | null) => void;
  setLoading: (loading: boolean) => void;
  setGenerating: (generating: boolean) => void;
  setError: (error: string | null) => void;
  
  // Research generation
  generateResearch: (projectId: string, reportTypes?: string[]) => Promise<void>;
  
  // Data fetching
  fetchResearchData: (projectId: string) => Promise<void>;
  
  // Reset state
  reset: () => void;
}

// Default research data structure
// const defaultResearchData: ResearchData = {
//   overview: {
//     top_suggestions: [],
//     strengths: [],
//     weaknesses: [],
//     buying_factors: [],
//     keywords: []
//   },
//   demographics: {
//     gender_distribution: {},
//     age_distribution: {},
//     geographic_distribution: {},
//     usage_scenarios: [],
//     purchase_drivers: [],
//     concerns_objections: [],
//     core_values: [],
//     purchase_behavior: [],
//     customer_psychology: []
//   },
//   competitor: {
//     direct_competitors: [],
//     indirect_competitors: [],
//     market_positioning: {},
//     strengths_weaknesses: {},
//     pricing_strategies: []
//   },
//   reviews: {
//     total_reviews: 0,
//     average_rating: 0,
//     positive_reviews: [],
//     negative_reviews: [],
//     sentiment_analysis: {},
//     common_themes: []
//   },
//   discovery: {
//     search_patterns: [],
//     discovery_channels: [],
//     conversion_optimization: [],
//     market_trends: []
//   },
//   keywords: {
//     primary_keywords: [],
//     long_tail_keywords: [],
//     competitor_keywords: [],
//     search_volume_data: {},
//     ranking_opportunities: []
//   }
// };

export const useResearchStore = create<ResearchState>()(
  devtools(
    (set, get) => ({
      // Initial state
      currentProject: null,
      researchData: null,
      isLoading: false,
      isGenerating: false,
      error: null,

      // Actions
      setCurrentProject: (project) => set({ currentProject: project }),
      setResearchData: (data) => set({ researchData: data }),
      setLoading: (loading) => set({ isLoading: loading }),
      setGenerating: (generating) => set({ isGenerating: generating }),
      setError: (error) => set({ error }),

      // Generate research for a project
      generateResearch: async (projectId: string, reportTypes = ['overview', 'demographics', 'competitor', 'reviews', 'discovery', 'keywords']) => {
        try {
          set({ isGenerating: true, error: null });
          
          // Call the API to generate research
          const response = await fetch(`${import.meta.env.VITE_API_URL || 'http://localhost:8000'}/api/generate-research`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              project_id: parseInt(projectId),
              report_types: reportTypes,
              background: true
            }),
          });

          if (!response.ok) {
            throw new Error('Failed to generate research');
          }

          const result = await response.json();
          
          if (result.success) {
            // Research generation started successfully - start polling for completion
            
            // Poll for research completion every 10 seconds for up to 5 minutes
            let attempts = 0;
            const maxAttempts = 30; // 5 minutes with 10-second intervals
            
            const pollForCompletion = async () => {
              if (attempts >= maxAttempts) {
                set({ 
                  isGenerating: false, 
                  error: 'Research generation is taking longer than expected. Please check back in a few minutes.' 
                });
                return;
              }
              
              attempts++;
              
              try {
                const dashboardResponse = await fetch(`${import.meta.env.VITE_API_URL || 'http://localhost:8000'}/api/research-dashboard/${projectId}`);
                
                if (dashboardResponse.ok) {
                  const dashboardData = await dashboardResponse.json();
                  const reports = dashboardData.reports || {};
                  const completedReports = Object.values(reports).filter((report: any) => report.status === 'completed');
                  
                  if (completedReports.length === reportTypes.length) {
                    // All reports completed - fetch the final data
                    set({ isGenerating: false });
                    // Fetch the complete data
                    await get().fetchResearchData(projectId);
                    return;
                  } else {
                    // Still processing - continue polling
                    setTimeout(pollForCompletion, 10000); // Poll every 10 seconds
                  }
                } else {
                  // Error in polling - continue anyway
                  setTimeout(pollForCompletion, 10000);
                }
              } catch (error) {
                setTimeout(pollForCompletion, 10000);
              }
            };
            
            // Start polling after 5 seconds
            setTimeout(pollForCompletion, 5000);
            
          } else {
            throw new Error(result.message || 'Failed to generate research');
          }
        } catch (error) {
          set({ 
            isGenerating: false, 
            error: error instanceof Error ? error.message : 'Unknown error occurred' 
          });
        }
      },

      // Fetch research data for a project
      fetchResearchData: async (projectId: string) => {
        try {
          set({ isLoading: true, error: null });
          
          const response = await fetch(`${import.meta.env.VITE_API_URL || 'http://localhost:8000'}/api/research-dashboard/${projectId}`);
          
          if (!response.ok) {
            throw new Error('Failed to fetch research data');
          }

          const data = await response.json();
          
          // Transform the API response to our internal format
          const transformOverviewData = (content: any) => {
            if (!content) return null;
            
            
            return {
              top_suggestions: content.top_suggestions?.map((item: any) => ({
                title: item.suggestion || item.title,
                description: item.impact || item.description,
                impact_percentage: item.benefit_percentage || item.impact_percentage,
                impact_level: item.priority || item.impact_level,
                tags: [item.priority || 'improvement'],
                reasoning: item.implementation || item.reasoning,
                priority: item.priority === 'High' ? 1 : item.priority === 'Medium' ? 2 : 3
              })) || [],
              strengths: content.strengths?.map((item: any) => ({
                feature: item.strength || item.feature,
                percentage: item.percentage,
                description: item.evidence || item.description,
                evidence: item.competitive_advantage || item.evidence,
                category: 'strength' as const
              })) || [],
              weaknesses: content.weaknesses?.map((item: any) => ({
                feature: item.weakness || item.feature,
                percentage: item.percentage,
                description: item.impact || item.description,
                evidence: item.improvement_opportunity || item.evidence,
                category: 'weakness' as const
              })) || [],
              buying_factors: content.purchase_triggers?.map((item: any) => ({
                factor: item.trigger || item.factor,
                percentage: item.percentage,
                impact: item.driver_type === 'Functional' ? 'High' : item.driver_type === 'Psychological' ? 'Medium' : 'Low',
                color: item.driver_type === 'Functional' ? 'green' : item.driver_type === 'Psychological' ? 'yellow' : 'red'
              })) || [],
              keywords: content.keyword_strategy?.map((item: any) => ({
                keyword: item.target_keyword || item.keyword,
                placement: item.ideal_placement || item.placement,
                action: item.action === 'Add' ? 'add' : 'replace',
                reasoning: item.rationale || item.reasoning,
                search_volume: item.search_volume,
                competition: item.competition
              })) || [],
              key_insights: content.key_insights?.map((insight: any) => ({
                insight: insight.insight,
                content_placement: insight.content_placement || [],
                strategic_importance: insight.strategic_importance,
                customer_evidence: insight.customer_evidence,
                impact_percentage: insight.impact_percentage,
                impact_level: insight.impact_level
              })) || []
            };
          };

          const transformDemographicsData = (content: any) => {
            if (!content) return null;
            
            // Extract gender distribution from customer segments
            const genderDistribution: Record<string, number> = {};
            content.customer_segments?.forEach((segment: any) => {
              if (segment.gender_distribution) {
                Object.entries(segment.gender_distribution).forEach(([gender, percentage]) => {
                  genderDistribution[gender] = (genderDistribution[gender] || 0) + (percentage as number);
                });
              }
            });
            
            // Extract age distribution from customer segments
            const ageDistribution: Record<string, number> = {};
            content.customer_segments?.forEach((segment: any) => {
              if (segment.age_range) {
                ageDistribution[segment.age_range] = (ageDistribution[segment.age_range] || 0) + (segment.percentage || 0);
              }
            });
            
            return {
              gender_distribution: Object.keys(genderDistribution).length > 0 ? genderDistribution : {},
              age_distribution: Object.keys(ageDistribution).length > 0 ? ageDistribution : {},
              geographic_distribution: {},
              usage_scenarios: Array.isArray(content.usage_scenarios)
                ? content.usage_scenarios.map((s: any) => ({
                    scenario: s.scenario,
                    percentage: s.percentage,
                    context: s.context,
                    key_reason: s.key_reason
                  }))
                : [],
              purchase_drivers: Array.isArray(content.purchase_drivers)
                ? content.purchase_drivers.map((d: any) => ({
                    trigger_event: d.trigger_event,
                    core_motivation: d.core_motivation,
                    driver_type: d.driver_type,
                    percentage: d.percentage
                  }))
                : [],
              concerns_objections: Array.isArray(content.concerns_objections)
                ? content.concerns_objections.map((c: any) => ({
                    concern: c.concern,
                    reason: c.reason,
                    percentage: c.percentage
                  }))
                : [],
              core_values: Array.isArray(content.core_values)
                ? content.core_values.map((v: any) => ({
                    value: v.value,
                    importance: v.importance,
                    aligned_icp: v.aligned_icp,
                    percentage: v.percentage
                  }))
                : [],
              purchase_behavior: content.purchase_drivers?.map((driver: any) => ({
                behavior: driver.trigger_event,
                percentage: driver.percentage,
                motivation: driver.core_motivation,
                type: driver.driver_type
              })) || [],
              customer_psychology: content.core_values?.map((value: any) => ({
                psychology: value.value,
                importance: value.importance,
                percentage: value.percentage
              })) || []
            };
          };

          const transformCompetitorData = (content: any) => {
            if (!content) return null;
            
            
            return {
              direct_competitors: content.direct_competitors?.map((c: any) => ({
                // basic identity
                name: c.name,
                asin: c.asin,
                tier: c.tier,
                // commerce
                price: c.price,
                quantity: c.quantity,
                // messaging
                headline_value_prop: c.headline_value_prop,
                market_positioning: c.market_positioning,
                target_icp: c.target_icp,
                // arrays
                repurchase_hooks: c.repurchase_hooks,
                functional_benefits: c.functional_benefits,
                formulation_elements: c.formulation_elements,
                usps: c.usps,
                drawbacks: c.drawbacks,
                flavor_variants: c.flavor_variants,
                positive_insights: c.positive_insights,
                recurring_problems: c.recurring_problems,
                purchase_reasons: c.purchase_reasons,
                usage_scenarios: c.usage_scenarios,
                // metrics
                review_metrics: c.review_metrics,
                rating: c.review_metrics?.average_rating ?? c.rating,
                reviews: c.review_metrics?.total_reviews ?? c.reviews,
                // media
                image: c.image,
                image_url: c.image_url
              })) || [],
              indirect_competitors: content.indirect_competitors?.map((competitor: any) => ({
                name: competitor.name,
                format: competitor.format,
                benefit: competitor.core_benefit,
                overlap: competitor.competition_overlap
              })) || [],
              market_positioning: {
                key_features: content.product_analysis?.key_features || [],
                functional_benefits: content.product_analysis?.functional_benefits || [],
                emotional_benefits: content.product_analysis?.emotional_benefits || [],
                usps: content.product_analysis?.usps || []
              },
              strengths_weaknesses: {
                strengths: content.product_analysis?.differentiators?.map((d: any) => d.differentiator) || [],
                weaknesses: content.product_analysis?.drawbacks || []
              },
              pricing_strategies: content.keyword_analysis?.primary_keywords?.map((keyword: any) => ({
                keyword: keyword.keyword,
                volume: keyword.search_volume,
                difficulty: keyword.ranking_difficulty,
                potential: keyword.sales_potential
              })) || []
            };
          };

          const transformReviewsData = (content: any) => {
            if (!content) return null;
            
            
            return {
              total_reviews: content.customer_sentiments?.total_reviews,
              average_rating: content.customer_sentiments?.average_rating,
              positive_reviews: content.customer_sentiments?.positive_sentiments?.map((sentiment: any) => ({
                sentiment: sentiment.sentiment,
                theme: sentiment.theme,
                percentage: sentiment.percentage,
                examples: sentiment.example_reviews
              })) || [],
              negative_reviews: content.customer_sentiments?.negative_sentiments?.map((sentiment: any) => ({
                sentiment: sentiment.sentiment,
                theme: sentiment.theme,
                percentage: sentiment.percentage,
                examples: sentiment.example_reviews
              })) || [],
              sentiment_analysis: {
                positive: content.customer_sentiments?.positive_sentiments?.length,
                negative: content.customer_sentiments?.negative_sentiments?.length,
                neutral: 1,
                trends: content.sentiment_trends?.map((trend: any) => trend.trend) || []
              },
              common_themes: content.review_patterns?.map((pattern: any) => ({
                theme: pattern.pattern,
                description: pattern.description,
                frequency: pattern.frequency,
                insight: pattern.insight
              })) || []
            };
          };

          const transformDiscoveryData = (content: any) => {
            if (!content) return null;
            
            
            return {
              search_patterns: content.product_features?.map((feature: any) => ({
                pattern: feature.feature,
                description: feature.description,
                emphasis: feature.emphasis_level,
                uniqueness: feature.uniqueness
              })) || [],
              discovery_channels: content.functional_benefits?.map((benefit: any) => ({
                channel: benefit,
                percentage: 0,
                description: '',
                effectiveness: ''
              })) || [],
              conversion_optimization: content.emotional_benefits?.map((benefit: any) => ({
                optimization: benefit,
                impact: '',
                strategy: '',
                roi: 0
              })) || [],
              market_trends: content.usps?.map((usp: any) => ({
                trend: usp.usp,
                growth: '',
                market_size: '',
                opportunity: usp.customer_value
              })) || []
            };
          };

          const transformKeywordsData = (content: any) => {
            if (!content) return null;
            
            
            return {
              primary_keywords: content.keyword_analysis?.primary_keywords?.map((keyword: any) => ({
                keyword: keyword.keyword,
                search_volume: keyword.search_volume,
                trend: keyword.trend,
                potential: keyword.sales_potential,
                difficulty: keyword.ranking_difficulty,
                relevance: keyword.relevance
              })) || [],
              long_tail_keywords: content.keyword_analysis?.long_tail_keywords?.map((keyword: any) => ({
                keyword: keyword.keyword,
                search_volume: keyword.search_volume,
                trend: keyword.trend,
                potential: keyword.sales_potential,
                difficulty: keyword.ranking_difficulty,
                relevance: keyword.relevance
              })) || [],
              competitor_keywords: content.keyword_analysis?.secondary_keywords?.map((keyword: any) => ({
                keyword: keyword.keyword,
                search_volume: keyword.search_volume,
                trend: keyword.trend,
                potential: keyword.sales_potential,
                difficulty: keyword.ranking_difficulty,
                relevance: keyword.relevance
              })) || [],
              search_volume_data: {
                'Total Searches': 0,
                'Primary Share': 0,
                'Long Tail Share': 0,
                'Competitor Share': 0
              },
              ranking_opportunities: content.keyword_opportunities?.map((opportunity: any) => ({
                opportunity: opportunity.opportunity,
                type: opportunity.type,
                description: opportunity.description,
                impact: opportunity.potential_impact,
                priority: opportunity.implementation_priority
              })) || []
            };
          };

          const transformedData: ResearchData = {
            overview: transformOverviewData(data.reports?.overview?.content),
            demographics: transformDemographicsData(data.reports?.demographics?.content),
            competitor: transformCompetitorData(data.reports?.competitor?.content),
            reviews: transformReviewsData(data.reports?.reviews?.content),
            discovery: transformDiscoveryData(data.reports?.discovery?.content),
            keywords: transformKeywordsData(data.reports?.keywords?.content),
          };

          // Debug logging
          console.log('Raw data:', {
            overview: data.reports?.overview?.content,
            demographics: data.reports?.demographics?.content,
            competitor: data.reports?.competitor?.content,
            reviews: data.reports?.reviews?.content,
            discovery: data.reports?.discovery?.content,
            keywords: data.reports?.keywords?.content,
          });
          
          console.log('Transformed data:', {
            overview: transformedData.overview,
            demographics: transformedData.demographics,
            competitor: transformedData.competitor,
            reviews: transformedData.reviews,
            discovery: transformedData.discovery,
            keywords: transformedData.keywords,
          });

          set({ 
            researchData: transformedData, 
            isLoading: false,
            currentProject: {
              id: data.project.id.toString(),
              name: data.project.project_name,
              brandName: data.project.brand_name,
              productUrl: data.project.amazon_listing_link,
              targetAudience: data.project.product_info,
              tone: 'Professional',
              goals: 'Increase sales and improve conversion',
              createdAt: data.project.created_at,
              updatedAt: data.last_updated
            }
          });
        } catch (error) {
          set({ 
            isLoading: false, 
            error: error instanceof Error ? error.message : 'Unknown error occurred',
            researchData: null
          });
        }
      },

      // Reset store state
      reset: () => set({
        currentProject: null,
        researchData: null,
        isLoading: false,
        isGenerating: false,
        error: null
      })
    }),
    {
      name: 'research-store',
    }
  )
); 